import React, { useEffect, useState } from "react";
import { collection, onSnapshot, updateDoc, doc, deleteDoc } from "firebase/firestore";
import { db } from "./firebase";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { getAuth } from "firebase/auth";
import { serverTimestamp } from "firebase/firestore";
import {
    BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
    PieChart, Pie, Cell, Legend
} from "recharts";
import { THEME_COLOR } from "./config/club";
//const THEME_COLOR = "#591094";

export default function AdminRegistrations() {
    const user = getAuth().currentUser;
    const navigate = useNavigate();
    const [registrations, setRegistrations] = useState([]);
    const [filter, setFilter] = useState("all");
    const [levelFilter, setLevelFilter] = useState("all");
    const [search, setSearch] = useState("");
    const [sortOrder, setSortOrder] = useState("desc");
    const [showReport, setShowReport] = useState(false);
    const [reportType, setReportType] = useState("revenue");
    const [showEditModal, setShowEditModal] = useState(false);
    const [editingRecord, setEditingRecord] = useState(null);

    const [editForm, setEditForm] = useState({
        fullName: "",
        contactNumber: "",
        level: "",
        jerseyName: "",
        jerseySize: "M"
    });

    useEffect(() => {
        const unsub = onSnapshot(collection(db, "registrations"), (snap) => {
            setRegistrations(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        });
        return () => unsub();
    }, []);

    const availableLevels = ["all", ...new Set(registrations.map(r => r.level).filter(Boolean))];

    const filtered = registrations.filter((r) => {
        if (r.isDeleted) return false;

        const name = r.personalInfo?.fullName?.toLowerCase() || "";
        const matchesSearch = name.includes(search.toLowerCase());

        const matchesStatus = filter === "all" ? true :
            filter === "paid" ? r.paymentStatus === "paid" :
                filter === "pending" ? r.paymentStatus === "pending_verification" :
                    r.paymentStatus === "unpaid";

        const matchesLevel = levelFilter === "all" ? true : r.level === levelFilter;

        return matchesSearch && matchesStatus && matchesLevel;
    });
    const sorted = [...filtered].sort((a, b) => {
        const dateA = a.createdAt?.toDate?.() || new Date(0);
        const dateB = b.createdAt?.toDate?.() || new Date(0);

        return sortOrder === "asc"
            ? dateA - dateB   // oldest first
            : dateB - dateA;  // newest first
    });
    const openEditModal = (record) => {
        setEditingRecord(record);

        setEditForm({
            fullName: record?.personalInfo?.fullName || "",
            contactNumber: record?.personalInfo?.contactNumber || "",
            level: record?.level || "",
            jerseyName: record?.jerseyForm?.items?.[0]?.name || "",
            jerseySize: record?.jerseyForm?.items?.[0]?.size || "M"
        });

        setShowEditModal(true);
    };
    const saveEdit = async () => {
        if (!editForm.fullName.trim()) {
            Swal.fire("Error", "Name is required", "error");
            return;
        }

        try {
            await updateDoc(doc(db, "registrations", editingRecord.id), {
                personalInfo: {
                    ...editingRecord.personalInfo,
                    fullName: editForm.fullName,
                    contactNumber: editForm.contactNumber
                },

                level: editForm.level,

                jerseyForm: {
                    ...editingRecord.jerseyForm,
                    items: [
                        {
                            ...editingRecord.jerseyForm?.items?.[0],
                            name: editForm.jerseyName,
                            size: editForm.jerseySize,
                            type: "Mandatory"
                        }
                    ]
                },

                updatedAt: serverTimestamp(),
                updatedBy: user?.uid || "unknown"
            });

            setShowEditModal(false);

            Swal.fire({
                toast: true,
                position: "top-end",
                icon: "success",
                title: "Updated",
                showConfirmButton: false,
                timer: 1500
            });

        } catch (err) {
            Swal.fire("Error", err.message, "error");
        }
    };
    const handleAction = async (actionType, id, name) => {
        // Check if system is in dark mode for Swal theme
        const isDarkMode = document.documentElement.getAttribute('data-bs-theme') === 'dark';

        const config = {
            title: "Are you sure?",
            text: `Modify ${name}'s registration?`,
            icon: "question",
            showCancelButton: true,
            confirmButtonColor: THEME_COLOR,
            cancelButtonColor: "#6c757d",
            borderRadius: '1.25rem',
            background: isDarkMode ? '#2b3035' : '#fff',
            color: isDarkMode ? '#fff' : '#000'
        };

        if (actionType === "delete") {
            config.icon = "warning";
            config.confirmButtonColor = "#dc3545";
        }

        const result = await Swal.fire(config);

        if (result.isConfirmed) {
            try {
                if (actionType === "delete") {
                    await updateDoc(doc(db, "registrations", id), {
                        isDeleted: true,
                        deletedAt: serverTimestamp(),
                        deletedBy: user?.uid || "unknown"
                    });
                } else {
                    if (actionType === "approve") {

                        // 🔥 Find the record
                        const record = registrations.find(r => r.id === id);

                        // const needsRef =
                        //     !record?.gcashReference || record?.paymentMethod === "later";
                        const needsRef = true;
                        let gcashReference = record?.gcashReference || "";

                        // 🔥 If missing OR pay later → require input
                        if (needsRef) {
                            const inputResult = await Swal.fire({
                                title: "Enter GCash Reference",
                                input: "text",
                                inputLabel: "Reference Number",
                                inputValue: record?.gcashReference || "",
                                inputPlaceholder: "e.g. 1234567890",
                                showCancelButton: true,
                                confirmButtonText: "Confirm Payment",
                                confirmButtonColor: THEME_COLOR,
                                inputValidator: (value) => {
                                    if (!value) return "Reference number is required";
                                },
                                background: isDarkMode ? '#2b3035' : '#fff',
                                color: isDarkMode ? '#fff' : '#000'
                            });

                            if (!inputResult.isConfirmed) return;

                            gcashReference = inputResult.value;
                        }

                        // ✅ Update with reference + mark paid
                        await updateDoc(doc(db, "registrations", id), {
                            paymentStatus: "paid",
                            gcashReference: gcashReference,
                            verifiedAt: serverTimestamp(),
                            verifiedBy: user?.uid || "unknown"
                        });

                    } else {
                        // revert
                        await updateDoc(doc(db, "registrations", id), {
                            paymentStatus: "unpaid"
                        });
                    }
                }
                Swal.fire({
                    toast: true,
                    position: "top-end",
                    icon: "success",
                    title: "Updated",
                    showConfirmButton: false,
                    timer: 1500,
                    background: isDarkMode ? '#2b3035' : '#fff',
                    color: isDarkMode ? '#fff' : '#000'
                });
            } catch (e) {
                Swal.fire("Error", e.message, "error");
            }
        }
    };
    const formatDate = (timestamp) => {
        if (!timestamp) return "N/A";

        const date = timestamp.toDate();

        return date.toLocaleString("en-PH", {
            timeZone: "Asia/Manila", // 🔥 THIS IS THE FIX
            month: "short",
            day: "numeric",
            year: "numeric",
            hour: "numeric",
            minute: "2-digit",
        });
    };
    const reportData = (() => {
        const paidList = sorted.filter(r => r.paymentStatus === "paid");

        // --- REVENUE ---
        const revenue = paidList.reduce((sum, r) => sum + (r.total || 0), 0);

        // --- STATUS ---
        const status = {
            paid: paidList.length,
            unpaid: sorted.filter(r => r.paymentStatus === "unpaid").length,
            pending: sorted.filter(r => r.paymentStatus === "pending_verification").length,
        };

        // --- LEVELS ---
        const byLevel = {};
        sorted.forEach(r => {
            if (!byLevel[r.level]) byLevel[r.level] = 0;
            byLevel[r.level]++;
        });

        // --- REG TYPES ---
        const byType = {};
        sorted.forEach(r => {
            const type = r.registrationType || "N/A";
            if (!byType[type]) byType[type] = 0;
            byType[type]++;
        });

        // --- JERSEYS ---
        const jerseysDetailed = [];
        const jerseySummary = {};

        const sizeSummary = {};
        sorted.forEach(r => {
            const items = r.jerseyForm?.items || [];

            items.forEach(item => {
                if ((item.type || "").toLowerCase() === "mandatory") {

                    const size = item.size || "N/A";
                    const name = item.name || "Jersey";
                    if (!sizeSummary[size]) sizeSummary[size] = 0;
                    sizeSummary[size]++;
                    // ✅ detailed list
                    jerseysDetailed.push({
                        player: r.personalInfo?.fullName,
                        itemName: name,
                        size: size,
                        type: item.type
                    });

                    // ✅ summary for chart
                    const key = `${size}`; // cleaner analytics (M, L, XL)
                    jerseySummary[key] = (jerseySummary[key] || 0) + 1;
                }
            });
        });

        return {
            total: sorted.length,
            revenue,
            status,
            byLevel,
            byType,
            jerseysDetailed,
            jerseySummary,
            sizeSummary
        };
    })();
    const chartColors = ["#591094", "#00d084", "#ffb900", "#dc3545", "#0dcaf0"];

    // LEVEL CHART
    const levelChartData = Object.entries(reportData.byLevel).map(([name, value]) => ({
        name,
        value
    }));

    // TYPE CHART
    const typeChartData = Object.entries(reportData.byType).map(([name, value]) => ({
        name,
        value
    }));

    // JERSEY CHART
    const jerseyChartData = Object.entries(reportData.jerseySummary || {}).map(([name, value]) => ({
        name,
        value
    }));

    // STATUS CHART
    const statusChartData = [
        { name: "Paid", value: reportData.status.paid },
        { name: "Pending", value: reportData.status.pending },
        { name: "Unpaid", value: reportData.status.unpaid }
    ];
    const exportCSV = () => {
        const levelOrder = [
            "Level A - Advanced",
            "Level B - High Intermediate",
            "Level C - Intermediate",
            "Level D - Low Intermediate",
            "Level E - High Beginner",
            "Level F - Beginner",
            "Level G - Novice"
        ];

        const playersSortedByLevel = [...filtered].sort((a, b) => {
            return levelOrder.indexOf(a.level) - levelOrder.indexOf(b.level);
        });
        let headers = [];
        let rows = [];

        if (reportType === "revenue") {
            headers = ["Name", "Status", "Total", "Reference"];

            rows = sorted.map(r => [
                r.personalInfo?.fullName,
                r.paymentStatus,
                r.total,
                r.gcashReference || ""
            ]);
        }

        if (reportType === "levels") {
            headers = ["Level", "Count"];
            rows = Object.entries(reportData.byLevel);
        }

        if (reportType === "jerseys") {
            headers = ["Player", "Jersey Name", "Size", "Type"];

            rows = reportData.jerseysDetailed.map(j => [
                j.player,
                j.itemName,
                j.size,
                j.type
            ]);
        }

        if (reportType === "types") {
            headers = ["Registration Type", "Count"];
            rows = Object.entries(reportData.byType);
        }
        if (reportType === "players") {
    headers = ["Name", "Level", "Contact", "Status"];

    rows = playersSortedByLevel.map(r => [
        r.personalInfo?.fullName || "",
        r.level || "",
        r.personalInfo?.contactNumber || "",
        r.paymentStatus || ""
    ]);
}
        const csvContent =
            [headers, ...rows]
                .map(e => e.map(v => `"${v}"`).join(","))
                .join("\n");

        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = url;
        link.download = `${reportType}_report.csv`;
        link.click();
    };
    return (
        <>
            {showReport && (
                <div
                    className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                    style={{ background: "rgba(0,0,0,0.6)", zIndex: 9999 }}
                >
                    <div
                        className="bg-body rounded-5 shadow-lg p-4 w-100 d-flex flex-column"
                        style={{ maxWidth: "700px", maxHeight: "90vh" }}
                    >

                        {/* HEADER */}
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h4 className="fw-black m-0">📊 Tournament Report</h4>
                            <button
                                className="btn btn-sm btn-outline-secondary rounded-pill"
                                onClick={() => setShowReport(false)}
                            >
                                ✕
                            </button>
                        </div>

                        {/* REPORT SELECTOR */}
                        <div className="mb-3 d-flex gap-2 flex-wrap">
                            {[
                                { key: "revenue", label: "💰 Revenue" },
                                { key: "levels", label: "🏸 Levels" },
                                { key: "jerseys", label: "👕 Jerseys" },
                                { key: "types", label: "📋 Types" },
                                { key: "players", label: "👥 Players" }
                            ].map(r => (
                                <button
                                    key={r.key}
                                    className={`btn rounded-pill px-3 fw-bold ${reportType === r.key ? "text-white" : "btn-outline-secondary"
                                        }`}
                                    style={reportType === r.key ? { backgroundColor: THEME_COLOR } : {}}
                                    onClick={() => setReportType(r.key)}
                                >
                                    {r.label}
                                </button>
                            ))}
                        </div>

                        {/* SCROLLABLE CONTENT */}
                        <div style={{ overflowY: "auto" }}>

                            {/* --- REVENUE --- */}
                            {reportType === "revenue" && (
                                <>
                                    {/* HEADER STATS */}
                                    <div className="row g-3 mb-3 text-center">

                                        {/* TOTAL REVENUE */}
                                        <div className="col-4">
                                            <div className="p-3 rounded-4 bg-dark text-white h-100">
                                                <div className="small opacity-75">Total Revenue</div>
                                                <div className="fw-black fs-5">₱{reportData.revenue}</div>
                                            </div>
                                        </div>

                                        {/* TO BE VERIFIED */}
                                        <div className="col-4">
                                            <div className="p-3 rounded-4 bg-warning text-dark h-100">
                                                <div className="small opacity-75">To Verify</div>
                                                <div className="fw-black fs-5">{reportData.status.pending}</div>
                                            </div>
                                        </div>

                                        {/* UNPAID */}
                                        <div className="col-4">
                                            <div className="p-3 rounded-4 bg-danger text-white h-100">
                                                <div className="small opacity-75">Unpaid</div>
                                                <div className="fw-black fs-5">{reportData.status.unpaid}</div>
                                            </div>
                                        </div>

                                    </div>

                                    {/* PIE CHART */}
                                    <ResponsiveContainer width="100%" height={250}>
                                        <PieChart>
                                            <Pie
                                                data={statusChartData}
                                                dataKey="value"
                                                nameKey="name"
                                                outerRadius={80}
                                                label
                                            >
                                                {statusChartData.map((entry, index) => (
                                                    <Cell key={index} fill={chartColors[index % chartColors.length]} />
                                                ))}
                                            </Pie>
                                            <Tooltip />
                                            <Legend />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </>
                            )}

                            {/* --- LEVELS --- */}
                            {reportType === "levels" && (
                                <>
                                    {/* KPI LIST */}
                                    <div className="bg-body-tertiary rounded-4 p-3 mb-3 border border-secondary-subtle">
                                        <div className="fw-bold mb-2">Level Breakdown</div>

                                        {Object.entries(reportData.byLevel)
                                            .sort((a, b) => {
                                                const getLevelLetter = (str) => str.split(" ")[1]; // "Level B - ..." → "B"
                                                return getLevelLetter(a[0]).localeCompare(getLevelLetter(b[0]));
                                            })
                                            .map(([level, count]) => (
                                                <div
                                                    key={level}
                                                    className="d-flex justify-content-between align-items-center py-1 border-bottom"
                                                >
                                                    <span className="small fw-bold">{level}</span>
                                                    <span className="badge bg-dark">{count}</span>
                                                </div>
                                            ))}
                                    </div>

                                    {/* TITLE */}
                                    <div className="fw-bold mb-2">Players per Level</div>

                                    {/* CHART */}
                                    <ResponsiveContainer width="100%" height={300}>
                                        <BarChart data={levelChartData}>
                                            <XAxis dataKey="name" />
                                            <YAxis />
                                            <Tooltip />
                                            <Bar dataKey="value" fill="#591094" />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </>
                            )}

                            {/* --- JERSEYS --- */}
                            {reportType === "jerseys" && (
                                <>
                                    {/* SIZE KPI LIST */}
                                    <div className="bg-body-tertiary rounded-4 p-3 mb-3 border border-secondary-subtle">
                                        <div className="fw-bold mb-2">Sizes Breakdown</div>

                                        {Object.entries(reportData.sizeSummary || {})
                                            .sort((a, b) => {
                                                const order = ["S", "M", "L", "XL", "2XL", "3XL"];
                                                return order.indexOf(a[0]) - order.indexOf(b[0]);
                                            })
                                            .map(([size, count]) => (
                                                <div
                                                    key={size}
                                                    className="d-flex justify-content-between align-items-center py-1 border-bottom"
                                                >
                                                    <span className="small fw-bold">{size}</span>
                                                    <span className="badge bg-success">{count}</span>
                                                </div>
                                            ))}
                                    </div>

                                    {/* CHART */}
                                    <div className="fw-bold mb-2">Jersey Distribution</div>

                                    <ResponsiveContainer width="100%" height={300}>
                                        <BarChart data={jerseyChartData}>
                                            <XAxis dataKey="name" />
                                            <YAxis />
                                            <Tooltip />
                                            <Bar dataKey="value" fill="#00d084" />
                                        </BarChart>
                                    </ResponsiveContainer>

                                    <hr />

                                    {/* DETAILS */}
                                    <div className="fw-bold mb-2">Detailed Orders</div>
                                    <div style={{ maxHeight: "200px", overflowY: "auto" }}>
                                        {reportData.jerseysDetailed.map((j, i) => (
                                            <div key={i} className="border-bottom py-2 small">
                                                <div className="fw-bold">{j.player}</div>
                                                <div className="text-muted">
                                                    {j.itemName} • Size: <b>{j.size}</b>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </>
                            )}

                            {/* --- TYPES --- */}
                            {reportType === "types" && (
                                <>
                                    <div className="fw-bold mb-2">Registration Types</div>

                                    <ResponsiveContainer width="100%" height={250}>
                                        <PieChart>
                                            <Pie
                                                data={typeChartData}
                                                dataKey="value"
                                                nameKey="name"
                                                outerRadius={80}
                                                label
                                            >
                                                {typeChartData.map((entry, index) => (
                                                    <Cell key={index} fill={chartColors[index % chartColors.length]} />
                                                ))}
                                            </Pie>
                                            <Tooltip />
                                            <Legend />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </>
                            )}
                        </div>

                        {/* FOOTER */}
                        <div className="d-flex gap-2 mt-3">
                            <button
                                className="btn w-100 rounded-pill fw-bold"
                                style={{ backgroundColor: THEME_COLOR, color: "white" }}
                                onClick={exportCSV}
                            >
                                ⬇ Export CSV
                            </button>

                            <button
                                className="btn btn-outline-secondary w-100 rounded-pill fw-bold"
                                onClick={() => setShowReport(false)}
                            >
                                Close
                            </button>
                        </div>

                    </div>
                </div>
            )}
            {showEditModal && (
                <div
                    className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                    style={{ background: "rgba(0,0,0,0.6)", zIndex: 9999 }}
                >
                <div className="modal fade show d-block" tabIndex="-1">
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content rounded-4 border-0 shadow-lg">

                            <div className="modal-header border-0">
                                <h5 className="modal-title fw-bold">Edit Registration</h5>
                                <button className="btn-close" onClick={() => setShowEditModal(false)}></button>
                            </div>

                            <div className="modal-body">

                                <div className="mb-3">
                                    <label className="form-label small">Full Name</label>
                                    <input
                                        className="form-control"
                                        value={editForm.fullName}
                                        onChange={(e) => setEditForm({ ...editForm, fullName: e.target.value })}
                                    />
                                </div>

                                <div className="mb-3">
                                    <label className="form-label small">Contact</label>
                                    <input
                                        className="form-control"
                                        value={editForm.contactNumber}
                                        onChange={(e) => setEditForm({ ...editForm, contactNumber: e.target.value })}
                                    />
                                </div>

                                <div className="mb-3">
                                    <label className="form-label small">Level</label>
                                    <select
                                        className="form-select"
                                        value={editForm.level}
                                        onChange={(e) => setEditForm({ ...editForm, level: e.target.value })}
                                    >
                                        <option>Level A - Advanced</option>
                                        <option>Level B - High Intermediate</option>
                                        <option>Level C - Intermediate</option>
                                        <option>Level D - Low Intermediate</option>
                                        <option>Level E - High Beginner</option>
                                        <option>Level F - Beginner</option>
                                        <option>Level G - Novice</option>
                                    </select>
                                </div>

                                <hr />

                                <div className="mb-3">
                                    <label className="form-label small">Jersey Name</label>
                                    <input
                                        className="form-control"
                                        value={editForm.jerseyName}
                                        onChange={(e) => setEditForm({ ...editForm, jerseyName: e.target.value.toUpperCase() })}
                                    />
                                </div>

                                <div className="mb-3">
                                    <label className="form-label small">Jersey Size</label>
                                    <select
                                        className="form-select"
                                        value={editForm.jerseySize}
                                        onChange={(e) => setEditForm({ ...editForm, jerseySize: e.target.value })}
                                    >
                                        {['12', '14', '16', '18', 'XS', 'S', 'M', 'L', 'XL', '2XL', '3XL', '4XL', '5XL'].map(s => (
                                            <option key={s}>{s}</option>
                                        ))}
                                    </select>
                                </div>

                            </div>

                            <div className="modal-footer border-0">
                                <button
                                    className="btn btn-light rounded-pill"
                                    onClick={() => setShowEditModal(false)}
                                >
                                    Cancel
                                </button>

                                <button
                                    className="btn rounded-pill text-white"
                                    style={{ backgroundColor: THEME_COLOR }}
                                    onClick={saveEdit}
                                >
                                    Save Changes
                                </button>
                            </div>

                        </div>
                    </div>
                </div>
                </div>
            )}
            <div className="container py-5 px-4 min-vh-100 transition-all">

                {/* --- BACK BUTTON --- */}
                <div className="mb-4">
                    <button
                        onClick={() => navigate("/admin/dashboard")}
                        className="btn d-inline-flex align-items-center gap-2 px-4 py-2 rounded-pill border-0 shadow-sm transition-all"
                        style={{ backgroundColor: `${THEME_COLOR}25`, color: THEME_COLOR }}
                    >
                        <i className="bi bi-arrow-left-short fs-4"></i>
                        <span className="fw-bold small">Back to Dashboard</span>
                    </button>
                </div>

                <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-end mb-5 gap-4">
                    <div>
                        <h1 className="fw-black m-0 text-body" style={{ letterSpacing: '-1.5px', fontSize: '2.5rem' }}>
                            Tournament <span style={{ color: THEME_COLOR }}>Registry</span>
                        </h1>
                        <p className="text-muted m-0 opacity-75 fw-medium">
                            Manage <span className="fw-bold text-primary-emphasis">{filtered.length}</span> active players
                        </p>
                    </div>
                    <div className="position-relative">
                        <i className="bi bi-search position-absolute top-50 translate-middle-y ms-3 text-muted"></i>
                        <input
                            type="text"
                            placeholder="Search players..."
                            className="form-control border-0 shadow-lg rounded-pill ps-5 py-3 bg-body-tertiary text-body"
                            style={{ minWidth: "300px", fontSize: '0.9rem' }}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </div>

                </div>


                {/* --- FILTER CONTROL PANEL --- */}
                <div className="row g-3 mb-5 align-items-center">
                    <div className="col-lg-12 d-flex align-items-center gap-2 justify-content-lg-end">
                        <button
                            className="btn rounded-pill px-4 py-2 fw-bold shadow-sm "
                            style={{ backgroundColor: THEME_COLOR, color: "white" }}
                            onClick={() => setShowReport(true)}
                        >
                            📊 View Report
                        </button>
                    </div>
                    <div className="col-lg-8">
                        <div className="bg-body-tertiary p-2 rounded-pill shadow-sm d-inline-flex flex-wrap gap-1 border border-secondary-subtle">
                            {["all", "paid", "pending", "unpaid"].map((f) => (
                                <button
                                    key={f}
                                    className={`btn btn-sm rounded-pill px-4 py-2 fw-bold border-0 ${filter === f ? "text-white shadow" : "bg-transparent text-muted hover-opacity"
                                        }`}
                                    style={filter === f ? { backgroundColor: THEME_COLOR } : {}}
                                    onClick={() => setFilter(f)}
                                >
                                    {(f === 'pending' ? 'FOR VERIFICATION' : f.toUpperCase())}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="col-lg-4 d-flex align-items-center gap-2 justify-content-lg-end">
                        <span className="small fw-bold text-muted text-uppercase op-75">Skill:</span>
                        <select
                            className="form-select border-0 shadow-sm rounded-pill fw-bold bg-body-tertiary text-body px-4 py-2 w-auto border border-secondary-subtle"
                            value={levelFilter}
                            onChange={(e) => setLevelFilter(e.target.value)}
                        >
                            <option value="all">All Brackets</option>
                            {availableLevels.filter(l => l !== 'all').map(lvl => (
                                <option key={lvl} value={lvl}>{lvl}</option>
                            ))}
                        </select>
                    </div>
                    <div className="d-flex align-items-center gap-2 justify-content-lg-end mt-2 mt-lg-0">
                        <span className="small fw-bold text-muted text-uppercase">Sort:</span>

                        <select
                            className="form-select border-0 shadow-sm rounded-pill fw-bold bg-body-tertiary text-body px-4 py-2 w-auto border border-secondary-subtle"
                            value={sortOrder}
                            onChange={(e) => setSortOrder(e.target.value)}
                        >
                            <option value="desc">Newest</option>
                            <option value="asc">Oldest</option>
                        </select>
                    </div>

                </div>

                {/* --- REGISTRATION CARDS --- */}
                <div className="row g-4">
                    {sorted.length > 0 ? (
                        sorted.map((r) => (
                            <div className="col-md-6 col-lg-4" key={r.id}>
                                <div className="card border-0 shadow-premium rounded-5 h-100 transition-all overflow-hidden bg-body-emphasis border border-secondary-subtle">
                                    <div className="card-body p-4 pt-5 position-relative d-flex flex-column">

                                        <div
                                            className="position-absolute top-0 start-0 w-100"
                                            style={{ height: '6px', backgroundColor: r.paymentStatus === 'paid' ? '#00d084' : '#ffb900' }}
                                        />

                                        <div className="d-flex justify-content-between align-items-start mb-4">
                                            <div>
                                                <h5 className="fw-black mb-1 text-body-emphasis" style={{ letterSpacing: '-0.5px' }}>
                                                    {r.personalInfo?.fullName}
                                                </h5>

                                                <span className="badge rounded-pill bg-body-secondary text-secondary-emphasis border fw-bold px-3 py-1" style={{ fontSize: '0.65rem' }}>
                                                    {r.level}
                                                </span>
                                                <hr></hr>
                                                <div className="mb-3">
                                                    <small className="text-muted fw-bold d-block">REGISTERED @</small>
                                                    <span className="small text-body">
                                                        {formatDate(r.createdAt)}
                                                    </span>
                                                </div>
                                            </div>
                                            <div className={`fw-black small text-uppercase py-1 px-3 rounded-pill ${r.paymentStatus === 'paid' ? 'text-success bg-success-subtle' : 'text-warning bg-warning-subtle'
                                                }`} style={{ fontSize: '0.7rem' }}>
                                                {r.paymentStatus === 'paid' ? 'Verified' : 'Pending'}
                                            </div>
                                        </div>

                                        <div className="bg-body-secondary p-3 rounded-4 mb-4 border border-secondary-subtle">
                                            <div className="d-flex justify-content-between align-items-center mb-2">
                                                <small className="text-muted fw-bold">TYPE</small>
                                                <span className="fw-black font-monospace text-body">{r.registrationType || "N/A"}</span>
                                            </div>
                                            <div className="d-flex justify-content-between align-items-center mb-2">
                                                <small className="text-muted fw-bold">REF NUMBER</small>
                                                <span className="fw-black font-monospace text-body">{r.gcashReference || "N/A"}</span>
                                            </div>
                                            <div className="d-flex justify-content-between align-items-center">
                                                <small className="text-muted fw-bold">TOTAL PAID</small>
                                                <span className="h5 mb-0 fw-black text-body-emphasis">₱{r.total}</span>
                                            </div>
                                        </div>

                                        <div className="d-flex gap-2 mt-auto pt-2">
                                            <button
                                                className="btn btn-outline-primary rounded-pill px-3"
                                                onClick={() => openEditModal(r)}
                                            >
                                                <i className="bi bi-pencil-fill"></i>
                                            </button>
                                            {r.paymentStatus !== "paid" ? (
                                                <button className="btn btn-success flex-grow-1 rounded-pill fw-bold py-2 shadow-sm" onClick={() => handleAction("approve", r.id, r.personalInfo.fullName)}>
                                                    Confirm
                                                </button>
                                            ) : (
                                                <button className="btn btn-outline-secondary flex-grow-1 rounded-pill fw-bold py-2 border-1" onClick={() => handleAction("unpaid", r.id, r.personalInfo.fullName)}>
                                                    Revert
                                                </button>
                                            )}
                                            <button className="btn btn-outline-danger rounded-pill px-3 border-1" onClick={() => handleAction("delete", r.id, r.personalInfo.fullName)}>
                                                <i className="bi bi-trash-fill"></i>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))
                    ) : (
                        <div className="col-12 py-5 my-5 text-center">
                            <div
                                className="d-inline-flex align-items-center justify-content-center rounded-circle mb-4"
                                style={{ width: '80px', height: '80px', backgroundColor: `${THEME_COLOR}20`, color: THEME_COLOR }}
                            >
                                <i className="bi bi-person-x fs-1"></i>
                            </div>
                            <h3 className="fw-black text-body mb-2" style={{ letterSpacing: '-1px' }}>No Players Found</h3>
                            <p className="text-muted mx-auto" style={{ maxWidth: '350px' }}>
                                No matches found for your current filters.
                            </p>
                            <button
                                className="btn btn-link fw-bold text-decoration-none"
                                style={{ color: THEME_COLOR }}
                                onClick={() => { setFilter('all'); setLevelFilter('all'); setSearch(''); }}
                            >
                                Reset Filters
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
}